import * as actionTypes from './actionTypes';
import { ClientActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Client } from '../../domain/Client';
import { ListResults } from '../../common/List/List';

export type ClientStateType = {
  clientsList: ListResults<Client> | null;
  clientsLoading: boolean;
  clientsError: HttpError;
  clientsListUpdateNeeded: boolean;
  clientCreateLoading: boolean;
  clientCreateError: HttpError;
  clientCreateSuccess: boolean;
  createdClient: Client | null;
  clientUpdateLoading: boolean;
  clientUpdateError: HttpError;
  clientUpdateSuccess: boolean;
  clientDeleteLoading: boolean;
  clientDeleteError: HttpError;
  clientDeleteSuccess: boolean;
  client: Client | null;
  clientLoading: boolean;
  clientError: HttpError;
  allClients: Client[];
  allClientsLoading: boolean;
  allClientsError: HttpError;
};

export type ClientActionType = ClientStateType & {
  type: ClientActionTypes;
};

export const initialState: ClientStateType = {
  clientsList: null,
  clientsLoading: true,
  clientsError: null,
  clientsListUpdateNeeded: false,
  clientCreateLoading: false,
  clientCreateError: null,
  clientCreateSuccess: false,
  createdClient: null,
  clientUpdateLoading: false,
  clientUpdateError: null,
  clientUpdateSuccess: false,
  clientDeleteLoading: false,
  clientDeleteError: null,
  clientDeleteSuccess: false,
  client: null,
  clientLoading: false,
  clientError: null,
  allClients: [],
  allClientsError: null,
  allClientsLoading: false,
};

const fetchClientsStart = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientsLoading: true,
});

const fetchClientsSuccess = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientsList: action.clientsList,
  clientsLoading: false,
  clientsError: null,
  clientsListUpdateNeeded: false,
});

const fetchClientsFail = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientsError: action.clientsError,
  clientsLoading: false,
});

const fetchAllClientsStart = (state: ClientStateType): ClientStateType => ({
  ...state,
  allClientsLoading: true,
});

const fetchAllClientsSuccess = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  allClients: action.allClients,
  allClientsLoading: false,
  allClientsError: null,
});

const fetchAllClientsFail = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  allClientsError: action.allClientsError,
  allClientsLoading: false,
});

const fetchClientStart = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientLoading: true,
  clientCreateSuccess: false,
  clientCreateError: null,
});

const fetchClientSuccess = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  client: action.client,
  clientLoading: false,
  clientError: null,
});

const fetchClientFail = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientError: action.clientError,
  clientLoading: false,
});

const createClientStart = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientCreateLoading: true,
});

const createClientSuccess = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientCreateLoading: false,
  clientCreateError: null,
  clientCreateSuccess: true,
  createdClient: action.createdClient,
});

const createClientFail = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientCreateLoading: false,
  clientCreateError: action.clientCreateError,
});

const createClientReset = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientCreateLoading: false,
  clientCreateError: null,
  clientCreateSuccess: false,
  createdClient: null,
});

const updateClientStart = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientUpdateLoading: true,
  clientUpdateSuccess: false,
});

const updateClientSuccess = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientUpdateLoading: false,
  clientUpdateError: null,
  clientUpdateSuccess: true,
});

const updateClientFail = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientUpdateLoading: false,
  clientUpdateError: action.clientUpdateError,
});

const deleteClientStart = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientDeleteLoading: true,
});

const deleteClientSuccess = (state: ClientStateType): ClientStateType => ({
  ...state,
  clientDeleteLoading: false,
  clientDeleteError: null,
  clientDeleteSuccess: true,
  clientsListUpdateNeeded: true,
});

const deleteClientFail = (
  state: ClientStateType,
  action: ClientActionType,
): ClientStateType => ({
  ...state,
  clientDeleteLoading: false,
  clientDeleteError: action.clientDeleteError,
});

const refreshClientList = (state: ClientStateType): ClientStateType => ({
  ...initialState,
  clientsListUpdateNeeded: true,
});

const resetClientStore = (state: ClientStateType): ClientStateType => ({
  ...initialState,
});

const completelyResetClientStore = (): ClientStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: ClientActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_CLIENTS_START:
      return fetchClientsStart(state);
    case actionTypes.FETCH_CLIENTS_SUCCESS:
      return fetchClientsSuccess(state, action);
    case actionTypes.FETCH_CLIENTS_FAIL:
      return fetchClientsFail(state, action);
    case actionTypes.FETCH_ALL_CLIENTS_START:
      return fetchAllClientsStart(state);
    case actionTypes.FETCH_ALL_CLIENTS_SUCCESS:
      return fetchAllClientsSuccess(state, action);
    case actionTypes.FETCH_ALL_CLIENTS_FAIL:
      return fetchAllClientsFail(state, action);
    case actionTypes.FETCH_CLIENT_START:
      return fetchClientStart(state);
    case actionTypes.FETCH_CLIENT_SUCCESS:
      return fetchClientSuccess(state, action);
    case actionTypes.FETCH_CLIENT_FAIL:
      return fetchClientFail(state, action);
    case actionTypes.CREATE_CLIENT_START:
      return createClientStart(state);
    case actionTypes.CREATE_CLIENT_SUCCESS:
      return createClientSuccess(state, action);
    case actionTypes.CREATE_CLIENT_FAIL:
      return createClientFail(state, action);
    case actionTypes.CREATE_CLIENT_RESET:
      return createClientReset(state);
    case actionTypes.UPDATE_CLIENT_START:
      return updateClientStart(state);
    case actionTypes.UPDATE_CLIENT_SUCCESS:
      return updateClientSuccess(state);
    case actionTypes.UPDATE_CLIENT_FAIL:
      return updateClientFail(state, action);
    case actionTypes.DELETE_CLIENT_START:
      return deleteClientStart(state);
    case actionTypes.DELETE_CLIENT_SUCCESS:
      return deleteClientSuccess(state);
    case actionTypes.DELETE_CLIENT_FAIL:
      return deleteClientFail(state, action);
    case actionTypes.REFRESH_CLIENT_LIST:
      return refreshClientList(state);
    case actionTypes.RESET_CLIENT_STORE:
      return resetClientStore(state);
    case actionTypes.LOGOUT:
      return completelyResetClientStore();
    default:
      return state;
  }
};

export default reducer;
